//src/pages/CreateFlashcards.jsx
import React, { useState } from "react";
import API from "../api";
import { useNavigate } from "react-router-dom";
import { useToast } from "../context/ToastContext";

export default function CreateFlashcards() {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [cards, setCards] = useState([{ question: "", answer: "" }]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { showToast } = useToast();

  const token = localStorage.getItem("token");
  if (!token) {
    return (
      <p className="text-center mt-20 text-red-600 text-lg font-semibold">
        ⚠️ Please login to create flashcards.
      </p>
    );
  }

  // ✅ Add new card
  const addCard = () => {
    setCards([...cards, { question: "", answer: "" }]);
  };

  // ✅ Remove card
  const removeCard = (index) => {
    const updated = [...cards];
    updated.splice(index, 1);
    setCards(updated);
  };

  // ✅ Handle input change
  const handleCardChange = (index, field, value) => {
    const updated = [...cards];
    updated[index][field] = value;
    setCards(updated);
  };

  // ✅ Handle form submit
  const handleSubmit = async (e) => {
  e.preventDefault();
  if (!title.trim() || cards.length === 0) {
    showToast("⚠️ Please add a title and at least one flashcard!");
    return;
  }

  setLoading(true);
  try {
    // Send each card individually to backend
    for (const card of cards) {
      await API.post(
        "/flashcards/add",
        {
          deck: title, // backend expects this field name
          question: card.question,
          answer: card.answer,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
    }

    showToast("✅ All flashcards added successfully!");
    navigate("/flashcards");
  } catch (err) {
    console.error("Error creating flashcards:", err);
    showToast("❌ Failed to create flashcards. Try again.");
  } finally {
    setLoading(false);
  }
};


  return (
    <div className="max-w-4xl mx-auto p-8 bg-white rounded-xl shadow-lg mt-10 border border-gray-200">
      <h2 className="text-3xl font-bold text-center text-blue-700 mb-4">
        🧠 Create Flashcards
      </h2>
      <p className="text-center text-gray-500 mb-6">
        Add a topic title, optional description, and your Q&A flashcards below.
      </p>

      <form onSubmit={handleSubmit}>
        {/* Flashcard Title */}
        <div className="mb-4">
          <label className="block font-semibold mb-1 text-gray-700">
            Flashcard Set Title
          </label>
          <input
            type="text"
            placeholder="e.g., Python Basics"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-blue-400 focus:outline-none"
          />
        </div>

        {/* Flashcard Description */}
        <div className="mb-6">
          <label className="block font-semibold mb-1 text-gray-700">
            Description (optional)
          </label>
          <textarea
            placeholder="e.g., Covers common Python syntax and definitions."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-blue-400 focus:outline-none"
          ></textarea>
        </div>

        {/* Flashcard List */}
        {cards.map((card, index) => (
          <div
            key={index}
            className="mb-5 p-4 bg-gray-50 border rounded-lg shadow-sm"
          >
            <div className="flex justify-between items-center mb-2">
              <h4 className="font-bold text-blue-600">Card {index + 1}</h4>
              {cards.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeCard(index)}
                  className="text-red-500 hover:underline text-sm"
                >
                  ❌ Remove
                </button>
              )}
            </div>

            {/* Question */}
            <input
              type="text"
              placeholder="Enter question..."
              value={card.question}
              onChange={(e) =>
                handleCardChange(index, "question", e.target.value)
              }
              className="w-full border border-gray-300 rounded px-3 py-2 mb-2 focus:ring-2 focus:ring-blue-400 focus:outline-none"
            />

            {/* Answer */}
            <input
              type="text"
              placeholder="Enter answer..."
              value={card.answer}
              onChange={(e) =>
                handleCardChange(index, "answer", e.target.value)
              }
              className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-blue-400 focus:outline-none"
            />
          </div>
        ))}

        {/* Add New Flashcard Button */}
        <div className="text-center mb-6">
          <button
            type="button"
            onClick={addCard}
            className="bg-blue-600 text-white px-5 py-2 rounded-lg hover:bg-blue-700 transition"
          >
            ➕ Add Another Flashcard
          </button>
        </div>

        {/* Submit Button */}
        <div className="text-center">
          <button
            type="submit"
            disabled={loading}
            className={`px-6 py-2 rounded-lg text-white font-semibold ${
              loading
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-green-600 hover:bg-green-700"
            }`}
          >
            {loading ? "Saving..." : "💾 Save Flashcards"}
          </button>
        </div>
      </form>
    </div>
  );
}
